import { useCallback } from 'react';
import { type RoutePath, type RouteSegments, useNavigationContainerRef } from 'expo-router';
import { Platform } from 'react-native';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a type-safe `reset` navigation function for the Expo Router setup.
 */
export const useNavigationReset = () => {
  const navigationRef = useNavigationContainerRef();

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const dispatchReset = useCallback(
    (state: ResetState) => {
      const targetState = findResetTargetState({
        navigationState: navigationRef.getRootState(),
        routeNames: state.routes.map(getRouteName),
      });

      if (!targetState?.key) {
        return;
      }

      // Expo Router ignores partial RESET payloads, so the target navigator keeps its own key,
      // route names and key sequence. Partial nested states are completed by the router.
      navigationRef.dispatch({
        type: 'RESET',
        target: targetState.key,
        payload: {
          ...targetState,
          index: state.index ?? state.routes.length - 1,
          routes: state.routes,
          stale: false,
        },
      } as never);
    },
    [navigationRef],
  );

  return useCallback(
    (state: ResetState) => {
      if (!navigationRef.isReady()) return;

      if (Platform.OS === 'web') {
        dispatchReset(state);
        return;
      }

      // NOTE: We defer on native to prevent conflicts between
      //       multiple modals.
      requestIdleCallback(() => {
        setTimeout(() => {
          dispatchReset(state);
        });
      });
    },
    [navigationRef, dispatchReset],
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Walks the focused branch down to the navigator that declares every route in
 * the reset, which is the root layout Stack for `(tabs)` and `(modals)` resets.
 */
function findResetTargetState(params: FindResetTargetStateParams): NavigationState | undefined {
  const { navigationState, routeNames } = params;

  const navigatorRouteNames = navigationState.routeNames ?? [];
  const isResetTarget = routeNames.every((routeName) => navigatorRouteNames.includes(routeName));

  if (isResetTarget) {
    return navigationState;
  }

  const focusedState = navigationState.routes[navigationState.index ?? 0]?.state;

  if (!focusedState) {
    return;
  }

  return findResetTargetState({ navigationState: focusedState, routeNames });
}

function getRouteName(route: ResetRoute) {
  return route.name;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ResetState = {
  index?: number;
  routes: ResetRoute[];
};

type ResetRoute = {
  name: RootLevelRouteName;
  state?: { routes: ResetNestedRoute[] };
  params?: Record<string, unknown>;
};

// NOTE: Nested-navigator screen names are local to their parent stack, so they
//       can't be statically narrowed from the app-wide `RoutePath` union.
type ResetNestedRoute = {
  name: string;
  state?: { routes: ResetNestedRoute[] };
  params?: Record<string, unknown>;
};

type RootLevelRouteName = RouteSegments<RoutePath>[number];

type NavigationState = {
  key?: string;
  index?: number;
  routeNames?: readonly string[];
  routes: readonly NavigationRoute[];
};

type NavigationRoute = {
  name: string;
  state?: NavigationState;
};

type FindResetTargetStateParams = {
  navigationState: NavigationState;
  routeNames: readonly string[];
};
